import { ColumnDef } from '@tanstack/react-table';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { canWrite } from 'app/access';
import { useAppSelector } from 'app/store';
import { selectUserInfo } from 'app/store/user';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'components/ui/alert-dialog';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { Checkbox } from 'components/ui/checkbox';
import { DataTable } from 'components/ui/data-table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';

import {
  TEAM_ROLES,
  TeamMember,
  TeamRole,
  useAddTeamMember,
  useRemoveTeamMember,
  useTeamMembers,
  useUpdateTeamMemberRoles,
} from './hooks/useTeamMembers';

const DEFAULT_ROLES: TeamRole[] = ['viewer'];

const ROLE_LABELS: Record<TeamRole, string> = {
  admin: 'Admin',
  developer: 'Developer',
  finance: 'Finance',
  operations: 'Operations',
  viewer: 'Viewer',
};

const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback;

const isHttpStatusError = (error: unknown, status: number) =>
  Boolean(
    error &&
      typeof error === 'object' &&
      'status' in error &&
      (error as { status?: unknown }).status === status,
  );

const TeamMembersEmptyState = ({ error }: { error: Error | null }) => {
  if (isHttpStatusError(error, 404)) {
    return (
      <div className="mx-auto max-w-xl space-y-2 text-center">
        <div className="font-medium text-[var(--text)]">Team not found</div>
        <div className="text-sm text-[var(--text-muted)]">
          No team was found for this account. Ask your organization's administrator to add you to a
          team. If your organization doesn't have a team yet, contact Rye to create one.
        </div>
      </div>
    );
  }

  if (error) {
    return <span className="text-destructive">Failed to load team members.</span>;
  }

  return <span className="text-muted-foreground">No team members yet.</span>;
};

const RoleBadges = ({ member }: { member: TeamMember }) => {
  if (member.isOwner) {
    return (
      <div className="flex justify-start">
        <Badge variant="brand">Owner</Badge>
      </div>
    );
  }

  if (!member.roles.length) {
    return <span className="text-sm text-[var(--text-muted)]">No roles</span>;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {member.roles.map((role) => (
        <Badge key={role} variant="neutral">
          {ROLE_LABELS[role] ?? role}
        </Badge>
      ))}
    </div>
  );
};

const RoleCheckboxes = ({
  selectedRoles,
  onChange,
  disabled,
  idPrefix,
}: {
  selectedRoles: TeamRole[];
  onChange: (roles: TeamRole[]) => void;
  disabled?: boolean;
  idPrefix: string;
}) => {
  const updateRole = (role: TeamRole, checked: boolean) => {
    if (checked) {
      onChange([...selectedRoles, role]);
      return;
    }

    onChange(selectedRoles.filter((selectedRole) => selectedRole !== role));
  };

  return (
    <div className="grid gap-3">
      {TEAM_ROLES.map((role) => {
        const checkboxId = `${idPrefix}-${role}`;

        return (
          <div key={role} className="flex items-center gap-3">
            <Checkbox
              id={checkboxId}
              checked={selectedRoles.includes(role)}
              disabled={disabled}
              onCheckedChange={(checked) => updateRole(role, checked === true)}
            />
            <Label htmlFor={checkboxId} className="cursor-pointer text-[var(--text)]">
              {ROLE_LABELS[role]}
            </Label>
          </div>
        );
      })}
    </div>
  );
};

const Page = () => {
  const userInfo = useAppSelector(selectUserInfo);
  const canManageTeam = canWrite(userInfo, 'team');
  const { members, loading, error } = useTeamMembers();
  const teamNotFound = isHttpStatusError(error, 404);
  const canShowTeamActions = canManageTeam && !teamNotFound;
  const addTeamMember = useAddTeamMember();
  const updateTeamMemberRoles = useUpdateTeamMemberRoles();
  const removeTeamMember = useRemoveTeamMember();

  const [addOpen, setAddOpen] = useState(false);
  const [addEmail, setAddEmail] = useState('');
  const [addRoles, setAddRoles] = useState<TeamRole[]>(DEFAULT_ROLES);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [editRoles, setEditRoles] = useState<TeamRole[]>(DEFAULT_ROLES);
  const [removingMember, setRemovingMember] = useState<TeamMember | null>(null);

  const resetAddForm = () => {
    setAddEmail('');
    setAddRoles(DEFAULT_ROLES);
  };

  const openEditDialog = (member: TeamMember) => {
    setEditingMember(member);
    setEditRoles(member.roles.length ? member.roles : DEFAULT_ROLES);
  };

  const handleAddUser = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!addRoles.length) {
      toast.error('Select at least one role');
      return;
    }

    try {
      await addTeamMember.mutateAsync({
        email: addEmail.trim(),
        roles: addRoles,
      });
      toast.success('User added');
      setAddOpen(false);
      resetAddForm();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to add user'));
    }
  };

  const handleUpdateRoles = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!editingMember) {
      return;
    }

    if (!editRoles.length) {
      toast.error('Select at least one role');
      return;
    }

    try {
      await updateTeamMemberRoles.mutateAsync({
        userId: editingMember.id,
        roles: editRoles,
      });
      toast.success('Roles updated');
      setEditingMember(null);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to update roles'));
    }
  };

  const handleRemoveUser = async () => {
    if (!removingMember) {
      return;
    }

    try {
      await removeTeamMember.mutateAsync(removingMember.id);
      toast.success('User removed');
      setRemovingMember(null);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to remove user'));
    }
  };

  const addDisabled = addTeamMember.isPending || addRoles.length === 0 || !addEmail.trim();
  const editDisabled = updateTeamMemberRoles.isPending || editRoles.length === 0;
  const columns: ColumnDef<TeamMember>[] = [
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({ row }) => (
        <span className="font-medium text-[var(--text)]">{row.original.email}</span>
      ),
      meta: { cellClassName: 'text-left whitespace-nowrap' },
    },
    {
      id: 'roles',
      header: 'Roles',
      cell: ({ row }) => <RoleBadges member={row.original} />,
      meta: { cellClassName: 'whitespace-normal' },
    },
    ...(canShowTeamActions
      ? [
          {
            id: 'actions',
            header: '',
            cell: ({ row }) =>
              row.original.isOwner ? null : (
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEditDialog(row.original)}>
                    <Pencil className="h-4 w-4" />
                    Edit
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setRemovingMember(row.original)}>
                    <Trash2 className="h-4 w-4" />
                    Remove
                  </Button>
                </div>
              ),
            meta: { cellClassName: 'text-right whitespace-nowrap' },
          } satisfies ColumnDef<TeamMember>,
        ]
      : []),
  ];

  return (
    <>
      <Card className="mt-[28px]">
        <CardHeader className="flex-auto flex-row justify-between gap-4">
          <div className="space-y-1">
            <CardTitle className="whitespace-break-spaces leading-[30px]">Team</CardTitle>
            <CardDescription>Team members and their roles</CardDescription>
          </div>
          {canShowTeamActions && (
            <Button onClick={() => setAddOpen(true)} size="sm">
              <Plus className="h-4 w-4" />
              Add user
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={members}
            loading={loading}
            pageSize={10}
            hideToolbar
            emptyState={<TeamMembersEmptyState error={error} />}
          />
        </CardContent>
      </Card>

      {canShowTeamActions && (
        <Dialog
          open={addOpen}
          onOpenChange={(open) => {
            setAddOpen(open);
            if (!open) {
              resetAddForm();
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add user</DialogTitle>
              <DialogDescription>Add an existing user to your team.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddUser} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="team-add-email">Email</Label>
                <Input
                  id="team-add-email"
                  type="email"
                  value={addEmail}
                  disabled={addTeamMember.isPending}
                  onChange={(event) => setAddEmail(event.target.value)}
                  required
                />
              </div>
              <div className="space-y-3">
                <Label>Roles</Label>
                <RoleCheckboxes
                  idPrefix="team-add-role"
                  selectedRoles={addRoles}
                  onChange={setAddRoles}
                  disabled={addTeamMember.isPending}
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setAddOpen(false)}
                  disabled={addTeamMember.isPending}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={addDisabled}>
                  {addTeamMember.isPending ? 'Adding...' : 'Add user'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {canShowTeamActions && (
        <Dialog open={!!editingMember} onOpenChange={(open) => !open && setEditingMember(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit roles</DialogTitle>
              <DialogDescription>{editingMember?.email}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateRoles} className="space-y-5">
              <div className="space-y-3">
                <Label>Roles</Label>
                <RoleCheckboxes
                  idPrefix="team-edit-role"
                  selectedRoles={editRoles}
                  onChange={setEditRoles}
                  disabled={updateTeamMemberRoles.isPending}
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditingMember(null)}
                  disabled={updateTeamMemberRoles.isPending}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={editDisabled}>
                  {updateTeamMemberRoles.isPending ? 'Saving...' : 'Save'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {canShowTeamActions && (
        <AlertDialog
          open={!!removingMember}
          onOpenChange={(open) => !open && setRemovingMember(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove user</AlertDialogTitle>
              <AlertDialogDescription>
                Remove {removingMember?.email} from your team.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={removeTeamMember.isPending}>Cancel</AlertDialogCancel>
              <Button
                variant="destructive"
                onClick={handleRemoveUser}
                disabled={removeTeamMember.isPending}
              >
                {removeTeamMember.isPending ? 'Removing...' : 'Remove'}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
};

export default Page;
